/**
 * apiAdapter
 *
 * The reclassification module came from the AIM project (`voice-ui-demo`), where
 * it talked to AssetPro through its own `utils/apiService.js` — a bare `fetch`
 * wrapper that read a token out of localStorage and pasted on its own tenant and
 * version headers.
 *
 * This app already has one configured axios instance (`src/util/Api.js`) that
 * handles the base URL per environment, attaches the Authorization header, and
 * routes 401s through the app's own session handling. Porting the fetch wrapper
 * as well would mean two HTTP clients with two auth stories in one app, and the
 * copy would quietly drift.
 *
 * So the module keeps its call sites unchanged and this adapter maps them onto
 * the app's client. Only the transport changes; the contract the engine was
 * written against — `{ success, data, status, error }`, never a throw — is
 * preserved exactly, because `applyChangesEngine` branches on `res.success` at
 * roughly a hundred call sites and a thrown error there would abort a partially
 * applied run.
 *
 * Endpoints are passed with a leading slash ('/asset-model'); the axios baseURL
 * already ends in '/v2/', so the slash is stripped rather than producing '//'.
 */

import http from '../../util/Api'

// The engine's uniform result shape. Errors are returned, never thrown.
const ok = (response) => ({
  success: true,
  data: response && response.data,
  status: response && response.status
})

const fail = (error) => {
  const res = error && error.response
  return {
    success: false,
    status: res && res.status,
    // Prefer the server's own message: the engine surfaces this text to the user
    // in its run log, and "Request failed with status code 400" tells nobody
    // which model or sub-type was rejected.
    error: (res && res.data && (res.data.message || res.data.error))
      || (error && error.message)
      || 'Request failed',
    data: res && res.data
  }
}

const path = (endpoint) => String(endpoint || '').replace(/^\/+/, '')

// headers must always be an object: the app's request interceptor assigns
// `config.headers.Authorization`, which throws on an undefined headers bag when
// a call site passes no options.
const hdrs = (options = {}) => ({ ...(options.headers || {}) })

export const apiGet = async (endpoint, options = {}) => {
  try {
    return ok(await http.get(path(endpoint), { headers: hdrs(options), params: options.params }))
  } catch (e) { return fail(e) }
}

export const apiPost = async (endpoint, body, options = {}) => {
  try {
    return ok(await http.post(path(endpoint), body, { headers: hdrs(options) }))
  } catch (e) { return fail(e) }
}

export const apiPut = async (endpoint, body, options = {}) => {
  try {
    return ok(await http.put(path(endpoint), body, { headers: hdrs(options) }))
  } catch (e) { return fail(e) }
}

export const apiDelete = async (endpoint, options = {}) => {
  try {
    return ok(await http.delete(path(endpoint), { headers: hdrs(options) }))
  } catch (e) { return fail(e) }
}

/**
 * The master-data endpoints (`/asset-type`, `/equipment-sub-type`,
 * `/asset-classification`, `/asset-model`) are multipart on this API because they
 * accept an image alongside the fields, which is why the engine builds FormData.
 *
 * The Content-Type header is deliberately NOT set: the browser has to generate it
 * itself so it carries the multipart boundary. Setting 'multipart/form-data' by
 * hand omits the boundary and the server cannot parse the body. axios's instance
 * default of 'application/json' is cleared to `undefined` for the same reason.
 */
const formDataConfig = (options = {}) => ({
  headers: { ...hdrs(options), 'Content-Type': undefined }
})

export const apiPostFormData = async (endpoint, formData, options = {}) => {
  try {
    return ok(await http.post(path(endpoint), formData, formDataConfig(options)))
  } catch (e) { return fail(e) }
}

export const apiPutFormData = async (endpoint, formData, options = {}) => {
  try {
    return ok(await http.put(path(endpoint), formData, formDataConfig(options)))
  } catch (e) { return fail(e) }
}

export default { apiGet, apiPost, apiPut, apiDelete, apiPostFormData, apiPutFormData }
